import React from "react";
import { Icon } from "@chakra-ui/react";
import { MdLocationCity, MdBuild, MdPalette } from "react-icons/md";
import ChipListSection from "./ChipListSection";

/**
 * Builds the chip configuration for the "all cities" special case.
 * @param {boolean} isMobile - Whether the viewport is mobile-sized
 * @returns {Object} Chip config with label and style properties
 */
function buildAllCitiesChipConfig(isMobile) {
    return {
        label: "Partout",
        style: {
            background: "rgba(76, 175, 80, 0.15)",
            backdropFilter: "blur(10px)",
            color: "rgba(76, 175, 80, 1)",
            border: "1px solid rgba(76, 175, 80, 0.4)",
            fontSize: isMobile ? "0.7rem" : "0.75rem",
            fontWeight: 600,
            height: "24px",
            borderRadius: "8px"
        }
    };
}

/**
 * Renders the cities chip list section for the service.
 * @param {Object} service - The service data object
 * @param {boolean} isMobile - Whether the viewport is mobile-sized
 * @returns {React.ReactElement}
 */
function renderCitiesSection(service, isMobile) {
    const isAll = service?.cities && service?.cities[0] === 'all';
    const allChipConfig = isAll ? buildAllCitiesChipConfig(isMobile) : null;

    return (
        <ChipListSection
            icon={<Icon as={MdLocationCity} color="rgba(76, 175, 80, 1)" boxSize="16px" />}
            title="Villes d'activites"
            tooltipText="Zones geographiques couvertes"
            items={isAll ? null : service?.cities}
            emptyText="Aucune ville definie"
            allChip={allChipConfig}
            isMobile={isMobile}
        />
    );
}

/**
 * Renders the materials chip list section for the service.
 * @param {Object} service - The service data object
 * @param {boolean} isMobile - Whether the viewport is mobile-sized
 * @returns {React.ReactElement}
 */
function renderMaterialsSection(service, isMobile) {
    const materials = service?.materials?.list_of_materials;
    const hasItems = materials?.length && materials[0] !== '';

    return (
        <ChipListSection
            icon={<Icon as={MdBuild} color="rgba(158, 158, 158, 1)" boxSize="16px" />}
            title="Liste du materiel"
            tooltipText="Equipement necessaire ou fourni"
            items={hasItems ? materials : []}
            emptyText="Aucun materiel defini"
            isMobile={isMobile}
        />
    );
}

/**
 * Renders the thematics chip list section for the service.
 * @param {Object} service - The service data object
 * @param {boolean} isMobile - Whether the viewport is mobile-sized
 * @returns {React.ReactElement}
 */
function renderThematicsSection(service, isMobile) {
    const thematicChipSx = {
        background: "rgba(103, 58, 183, 0.15)",
        backdropFilter: "blur(10px)",
        color: "rgba(103, 58, 183, 1)",
        border: "1px solid rgba(103, 58, 183, 0.4)",
        fontSize: isMobile ? "0.7rem" : "0.75rem",
        fontWeight: 600,
        height: "24px",
        borderRadius: "8px"
    };

    return (
        <ChipListSection
            icon={<Icon as={MdPalette} color="rgba(103, 58, 183, 1)" boxSize="16px" />}
            title="Thematiques"
            tooltipText="Thematiques du service"
            items={service?.thematics}
            emptyText="Aucune thematique definie"
            chipStyle={thematicChipSx}
            isMobile={isMobile}
        />
    );
}

export { renderCitiesSection, renderMaterialsSection, renderThematicsSection };
